import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import useGetSBC from './queries/useGetSBC';
import useCreateSBC from './queries/useCreateSBC';
import useUpdateSBC from './queries/useUpdateSBC';

import FormBase from '@/components/forms/new/FormBase';
import { LoadingIndicator } from 'components/v4';
import { addHeader, editHeader, initialState } from './constants';
import SBCPriorityField from './formFields/SBCPriorityField';
import SBCNameField from './formFields/SBCNameField';
import SBCLocationDropdown from './formFields/SBCLocationDropdown';
import SBCActiveToggle from './formFields/SBCActiveToggle';

/*
 * Rendered inside LocationEdit's "SBC" tab panel, which is a sibling of the
 * "Location Details" tab panel's own FormBase — not nested inside it — so
 * this FormBase's <form> and Save/Cancel row are independent of the
 * Location's, matching how each entity's own form should behave regardless
 * of which tab is active.
 */
const SBCForm = ({ mode, locationId, sbcId, onDone, onCancel }) => {
    const isEditMode = mode === 'Edit';
    const { darkMode } = useSelector((state) => state.settings);

    const {
        data: sbcData,
        isLoading: sbcLoading,
        isFetching: sbcFetching,
    } = useGetSBC(sbcId, isEditMode);

    const handleSuccess = () => {
        toast.success(
            isEditMode
                ? 'SBC updated successfully!'
                : 'SBC created successfully!',
        );
        onDone();
    };

    const handleError = (error) => {
        console.error(
            `Failed to ${isEditMode ? 'update' : 'create'} SBC:`,
            error,
        );
        toast.error(
            `Failed to ${isEditMode ? 'update' : 'create'} SBC. Please try again.`,
        );
    };

    const { mutate: createSBC, isPending: createPending } = useCreateSBC(
        handleSuccess,
        handleError,
    );
    const { mutate: updateSBC, isPending: updatePending } = useUpdateSBC(
        handleSuccess,
        handleError,
    );

    const finalFormSubmit = async (values) => {
        if (
            !values.name ||
            !/^[a-zA-Z0-9-]+$/.test(values.name) ||
            !values.locationID
        ) {
            console.error(
                'SBC name (alphanumeric/hyphen) and Location are required',
            );
            return;
        }
        const payload = {
            ...sbcData,
            priority: values.priority,
            name: values.name,
            locationID: values.locationID,
            isActive: values.isActive,
        };
        if (isEditMode) {
            updateSBC({ ...payload, id: sbcId });
        } else {
            createSBC(payload);
        }
    };

    if (isEditMode && (sbcLoading || sbcFetching)) {
        return <LoadingIndicator />;
    }

    const editInitialValues = isEditMode
        ? {
              priority: sbcData?.priority ?? '',
              name: sbcData?.name || '',
              locationID: sbcData?.locationID || locationId,
              isActive: sbcData?.isActive ?? true,
          }
        : { ...initialState, locationID: locationId };

    const isPending = isEditMode ? updatePending : createPending;

    const formFields = (errors, touched, values, setFieldValue) => [
        <SBCPriorityField key="priority" darkMode={darkMode} />,
        <SBCNameField
            key="name"
            errors={errors}
            touched={touched}
            darkMode={darkMode}
        />,
        <SBCLocationDropdown
            key="locationID"
            darkMode={darkMode}
            values={values}
            setFieldValue={setFieldValue}
        />,
        <SBCActiveToggle
            key="isActive"
            darkMode={darkMode}
            values={values}
            setFieldValue={setFieldValue}
        />,
    ];

    return (
        <FormBase
            backLink={onCancel}
            backText="Cancel"
            isSubmitDisabled={isPending}
            isLoading={isPending}
            header={isEditMode ? editHeader : addHeader}
            initialValues={editInitialValues}
            finalFormSubmit={finalFormSubmit}>
            {({ errors, touched, values, setFieldValue }) =>
                formFields(errors, touched, values, setFieldValue)
            }
        </FormBase>
    );
};

export default SBCForm;
